import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faEye,
  faEyeSlash,
  faArrowUpRightFromSquare,
  faBell,
  faBullhorn,
  faRotateLeft,
  faTriangleExclamation,
  faRotate,
  faCircleCheck,
  faEnvelope,
  faMedal,
  faStar,
  faGift,
  faUsers,
  faHourglassHalf,
  faCalendarDays,
} from '@fortawesome/free-solid-svg-icons';
import Participants from '../components/Competitions/Participants';
import Countdown from '../components/Competitions/Countdown';
import Badges from '../components/Competitions/Badges';
import CompetitionsBoard, { ReadMore, Teams, PeopleHead, LEVEL, PLACE, day as compDay, endAt } from '../components/Competitions/CompetitionsBoard';
import '../components/Competitions/CompetitionsBoard.css';
import './Competitions.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const LEVELS = [
  ['all', 'All students'],
  ['little', 'Little (6–8)'],
  ['junior', 'Junior (9+)'],
];
const STATUS = {
  upcoming: ['Coming soon', 'is-info'],
  open: ['Open for projects', 'is-good'],
  judging: ['Teachers voting', 'is-warn'],
  finished: ['Winners published', 'is-off'],
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// new challenge: this month, from today to the last day of the month
const newDraft = () => {
  const now = new Date();
  return {
    id: null,
    title: `${now.toLocaleDateString('en-US', { month: 'long' })} challenge: `,
    level: 'all',
    startDate: iso(now),
    startTime: '',
    endDate: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    endTime: '21:00', // 9 PM: before kids sleep, not at midnight
    prize: 'Winner badge on the dashboard + shout-out on our Instagram',
    description: '',
  };
};

/* Top 3 by average score (more votes first on a tie) */
const suggestRanks = (entries) => {
  const out = {};
  entries
    .filter((e) => !e.hidden && e.avg != null)
    .sort((a, b) => b.avg - a.avg || b.votes - a.votes)
    .slice(0, 3)
    .forEach((e, i) => {
      out[e.id] = i + 1;
    });
  return out;
};

/* One project, in the same card look as the student / teacher pages + the admin tools */
const AdminEntry = ({ e, rank, activeTeachers, busy, onRank, onHide }) => (
  <li className={`rmcmp-entry rmacmp-entry ${rank ? `is-rank-${rank}` : ''} ${e.hidden ? 'is-hidden' : ''}`}>
    {e.rank && (
      <span className="rmcmp-medal">
        <FontAwesomeIcon icon={e.rank === 1 ? faTrophy : faMedal} /> {PLACE[e.rank]}
      </span>
    )}
    <div className="rmcmp-entry-who">
      {e.photo && <img src={e.photo} alt="" loading="lazy" onError={(ev) => (ev.currentTarget.style.display = 'none')} />}
      <div>
        <h4>{e.title || 'Project'}</h4>
        <p className="rmcmp-by">
          by{' '}
          <a href={`/admin/StudentProfile/${e.studentId}`} target="_blank" rel="noopener noreferrer">
            {e.name}
          </a>
          {e.teacher && <span className="rmcmp-team"> · with {e.teacher}</span>}
        </p>
        <Badges b={e.badges} />
      </div>
    </div>
    {e.note && <p className="rmcmp-note">{e.note}</p>}
    <a className="rmcmp-open" href={e.link} target="_blank" rel="noopener noreferrer">
      Open project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
    </a>
    <div className="rmacmp-entry-tools">
      <span className="rmacmp-score" title="Average score from the other teachers">
        <FontAwesomeIcon icon={faStar} /> {e.avg != null ? <b>{e.avg}</b> : '—'}
        <small>
          {e.votes}/{activeTeachers} votes
        </small>
      </span>
      <select value={rank || ''} onChange={(ev) => onRank(e.id, parseInt(ev.target.value, 10) || null)} disabled={e.hidden || busy} aria-label={`Place for ${e.name}`}>
        <option value="">No place</option>
        <option value="1">🥇 1st</option>
        <option value="2">🥈 2nd</option>
        <option value="3">🥉 3rd</option>
      </select>
      <button
        type="button"
        className="rmacmp-icon"
        onClick={onHide}
        disabled={busy}
        aria-label={e.hidden ? 'Show project' : 'Hide project'}
        title={e.hidden ? 'Hidden from students and teachers: show it' : 'Hide from students and teachers'}
      >
        <FontAwesomeIcon icon={e.hidden ? faEyeSlash : faEye} />
      </button>
    </div>
    {e.hidden && <p className="rmacmp-hidden-note">Hidden from students and teachers</p>}
  </li>
);

const CompetitionCard = ({ c, activeTeachers, onEdit, onChanged, say }) => {
  const [ranks, setRanks] = useState(() => (c.status === 'finished' ? Object.fromEntries(c.entries.filter((e) => e.rank).map((e) => [e.id, e.rank])) : suggestRanks(c.entries)));
  const [busy, setBusy] = useState(false);
  const [label] = STATUS[c.status] || STATUS.upcoming;
  const sorted = [...c.entries].sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1) || b.votes - a.votes);
  const placed = sorted.filter((e) => ranks[e.id]).sort((a, b) => ranks[a.id] - ranks[b.id]);
  const rest = sorted.filter((e) => !ranks[e.id]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) say(msg);
      onChanged();
    } catch (err) {
      say(err.response?.data?.error || 'Something went wrong.', true);
    } finally {
      setBusy(false);
    }
  };

  const setRank = (entryId, rank) =>
    setRanks((r) => {
      const n = { ...r };
      Object.keys(n).forEach((k) => {
        if (rank && n[k] === rank) delete n[k]; // one entry per place
      });
      if (rank) n[entryId] = rank;
      else delete n[entryId];
      return n;
    });

  const entry = (e) => (
    <AdminEntry
      key={e.id}
      e={e}
      rank={ranks[e.id]}
      activeTeachers={activeTeachers}
      busy={busy}
      onRank={setRank}
      onHide={() => run(() => axios.put(`${API}/api/admin/competitions/entry/${e.id}`, { hidden: !e.hidden }, auth()), e.hidden ? 'Project shown.' : 'Project hidden.')}
    />
  );

  return (
    <article className={`rmcmp-card is-${c.status}`}>
      <div className="rmcmp-card-head">
        <span className="rmcmp-level">{LEVEL[c.level] || LEVEL.all}</span>
        <span className="rmcmp-when">
          <FontAwesomeIcon icon={c.status === 'open' ? faHourglassHalf : faCalendarDays} />
          {c.status === 'upcoming' && `Starts ${compDay(c.startDate)} · ends ${compDay(c.endDate)}${endAt(c)}`}
          {c.status === 'open' && `Projects until ${compDay(c.endDate, { weekday: 'short', month: 'short', day: 'numeric' })}${endAt(c)}`}
          {c.status === 'judging' && 'Teachers are voting'}
          {c.status === 'finished' && `${compDay(c.startDate)} – ${compDay(c.endDate)} · ${label}`}
        </span>
        <div className="rmacmp-actions">
          <button type="button" onClick={() => onEdit(c)} aria-label="Edit challenge" title="Edit">
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button
            type="button"
            className="is-danger"
            aria-label="Delete challenge"
            title="Delete"
            disabled={busy}
            onClick={() =>
              window.confirm(`Delete “${c.title}” and its ${c.entries.length} projects?`) &&
              run(() => axios.delete(`${API}/api/admin/competitions/${c.id}`, auth()), 'Challenge deleted.')
            }
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>
      <h3>{c.title}</h3>
      {c.description && <ReadMore text={c.description} />}
      {c.prize && (
        <p className="rmcmp-prize">
          <FontAwesomeIcon icon={faGift} /> {c.prize}
        </p>
      )}

      {c.status === 'upcoming' && <Countdown to={c.startsAt} label="Starts in" onDone={onChanged} />}
      {c.status === 'open' && <Countdown to={c.endsAt} label="Time left to send projects" onDone={onChanged} />}

      {(c.status === 'upcoming' || c.status === 'open') && (
        <>
          <PeopleHead c={c} mode="teacher" />
          <Participants list={c.participants || []} teams={c.teams} />
        </>
      )}

      {(c.status === 'judging' || c.status === 'finished') && c.teams?.length > 0 && (
        <>
          <h4 className="rmcmp-sub">
            <FontAwesomeIcon icon={faUsers} /> Teams
          </h4>
          <Teams c={c} />
        </>
      )}

      {c.status === 'judging' && c.entries.length > 0 && (
        <p className="rmcmp-hint">The top 3 by average score are picked for you. Change the places if you want, then publish the winners.</p>
      )}

      {placed.length > 0 && (
        <>
          <h4 className="rmcmp-sub">
            <FontAwesomeIcon icon={faTrophy} /> {c.status === 'finished' ? 'Winners' : 'Winners (not published yet)'}
          </h4>
          <ul className="rmcmp-entries rmcmp-entries--winners">{placed.map(entry)}</ul>
        </>
      )}
      {rest.length > 0 && (
        <>
          <h4 className="rmcmp-sub">{placed.length ? 'All projects' : `${rest.length} ${rest.length === 1 ? 'project' : 'projects'}`}</h4>
          <ul className="rmcmp-entries">{rest.map(entry)}</ul>
        </>
      )}
      {c.entries.length === 0 && (
        <p className="rmcmp-hint">{c.status === 'upcoming' ? 'Students can send projects from the start date.' : c.status === 'open' ? 'No projects yet.' : 'No projects were sent for this challenge.'}</p>
      )}

      <div className="rmacmp-foot">
        {(c.status === 'upcoming' || c.status === 'open') && (
          <Link className="rmacmp-btn rmacmp-btn--ghost" to={`/Admin/sendemails?competition=${c.id}`}>
            <FontAwesomeIcon icon={faEnvelope} /> Email all parents
          </Link>
        )}
        {c.status === 'judging' && (
          <button type="button" className="rmacmp-btn rmacmp-btn--ghost" disabled={busy} onClick={() => run(() => axios.post(`${API}/api/admin/competitions/${c.id}/notify-teachers`, {}, auth()), 'Teachers notified.')}>
            <FontAwesomeIcon icon={faBell} /> Ask teachers to vote
          </button>
        )}
        {(c.status === 'judging' || c.status === 'finished') && c.entries.length > 0 && (
          <button
            type="button"
            className="rmacmp-btn"
            disabled={busy || !Object.values(ranks).includes(1)}
            onClick={() => run(() => axios.post(`${API}/api/admin/competitions/${c.id}/publish`, { ranks }, auth()), 'Winners published! Everyone can see them now.')}
            title={!Object.values(ranks).includes(1) ? 'Pick at least the 1st place' : ''}
          >
            <FontAwesomeIcon icon={faBullhorn} /> {c.status === 'finished' ? 'Update winners' : 'Publish winners'}
          </button>
        )}
        {c.status === 'finished' && (
          <button type="button" className="rmacmp-btn rmacmp-btn--ghost" disabled={busy} onClick={() => run(() => axios.post(`${API}/api/admin/competitions/${c.id}/unpublish`, {}, auth()), 'Winners hidden again.')}>
            <FontAwesomeIcon icon={faRotateLeft} /> Unpublish
          </button>
        )}
        {c.status === 'open' && <p className="rmacmp-dim">Projects stay private until the end date. Then teachers can vote.</p>}
      </div>
    </article>
  );
};

const GROUPS = [
  ['open', 'Open now'],
  ['judging', 'Teachers are voting'],
  ['upcoming', 'Coming soon'],
  ['finished', 'Past challenges'],
];

const Competitions = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [toast, setToast] = useState(null);
  // "students" = the page exactly as students see it, "manage" = edit, scores, publish winners
  const [view, setViewState] = useState(() => {
    try {
      return localStorage.getItem('rm-admin-comp-view') || 'students';
    } catch {
      return 'students';
    }
  });
  const setView = (v) => {
    setViewState(v);
    try {
      localStorage.setItem('rm-admin-comp-view', v);
    } catch {
      /* private mode */
    }
  };

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/competitions`, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the competitions.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!draft) return undefined;
    const onKey = (e) => e.key === 'Escape' && !saving && setDraft(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draft, saving]);

  const say = (message, bad = false) => setToast({ message, bad });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      if (draft.id) await axios.put(`${API}/api/admin/competitions/${draft.id}`, draft, auth());
      else await axios.post(`${API}/api/admin/competitions`, draft, auth());
      setDraft(null);
      say(draft.id ? 'Challenge saved.' : 'Challenge created. Students will see it on the start date.');
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Couldn’t save the challenge.');
    } finally {
      setSaving(false);
    }
  };

  if (error && !data) {
    return (
      <div className="rmacmp">
        <div className="rmacmp-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmacmp-btn" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="rmacmp" aria-busy="true"><div className="rmacmp-skel" /></div>;

  const comps = data.competitions || [];

  return (
    <div className="rmacmp rmcmp">
      <header className="rmacmp-header">
        <div className="rmcmp-head">
          <span className="rmcmp-eyebrow">
            <FontAwesomeIcon icon={faTrophy} /> Competitions
          </span>
          <h1>Monthly challenges</h1>
          <p>One challenge a month. Students send a project link while it’s open; after the end date every teacher scores the projects 1–10, then you publish the top 3. Students and teachers see first name + last initial.</p>
        </div>
        <div className="rmacmp-header-actions">
          <div className="rmacmp-view" role="radiogroup" aria-label="View">
            {[
              ['students', 'Student view'],
              ['manage', 'Manage'],
            ].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={view === k} className={view === k ? 'is-on' : ''} onClick={() => setView(k)}>
                {l}
              </button>
            ))}
          </div>
          <button type="button" className="rmacmp-btn" onClick={() => { setFormError(''); setDraft(newDraft()); }}>
            <FontAwesomeIcon icon={faPlus} /> New challenge
          </button>
        </div>
      </header>

      {view === 'students' ? (
        <>
          <p className="rmacmp-preview-note">This is what students see (all levels). Switch to <b>Manage</b> to edit, see scores and publish the winners.</p>
          {/* reloads when a challenge is created / edited / published here */}
          <CompetitionsBoard
            key={comps.map((c) => [c.id, c.status, c.title, c.startDate, c.endDate, c.endTime, c.entries.length].join(':')).join('|')}
            mode="admin"
            embedded
          />
        </>
      ) : comps.length === 0 ? (
        <div className="rmcmp-empty">
          <FontAwesomeIcon icon={faTrophy} />
          <p>No challenges yet. Create the first one: students and teachers see it on their Competitions page.</p>
        </div>
      ) : (
        GROUPS.map(([key, title]) => {
          const list = comps.filter((c) => c.status === key);
          if (!list.length) return null;
          return (
            <section key={key} className="rmcmp-group">
              <h2>{title}</h2>
              {list.map((c) => (
                <CompetitionCard
                  key={`${c.id}-${c.status}-${c.entries.length}`}
                  c={c}
                  activeTeachers={data.activeTeachers}
                  onEdit={(x) => { setFormError(''); setDraft({
                      id: x.id,
                      title: x.title,
                      level: x.level,
                      startDate: x.startDate,
                      startTime: x.startTime && x.startTime !== '00:00' ? x.startTime : '',
                      endDate: x.endDate,
                      endTime: x.endTime && x.endTime !== '23:59' ? x.endTime : '',
                      prize: x.prize,
                      description: x.description,
                    }); }}
                  onChanged={load}
                  say={say}
                />
              ))}
            </section>
          );
        })
      )}

      {draft && (
        <div className="rmacmp-overlay" onClick={() => !saving && setDraft(null)}>
          <form className="rmacmp-modal" role="dialog" aria-modal="true" aria-label="Challenge" onClick={(e) => e.stopPropagation()} onSubmit={save}>
            <header>
              <h2>
                <FontAwesomeIcon icon={faTrophy} /> {draft.id ? 'Edit challenge' : 'New challenge'}
              </h2>
              <button type="button" className="rmacmp-x" onClick={() => setDraft(null)} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmacmp-modal-body">
              <label className="rmacmp-field">
                <span>Title</span>
                <input type="text" maxLength={120} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} required disabled={saving} />
              </label>
              <label className="rmacmp-field rmacmp-for">
                <span>For</span>
                <select value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value })} disabled={saving}>
                  {LEVELS.map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <div className="rmacmp-dates">
                <fieldset className="rmacmp-date-card">
                  <legend>Opens</legend>
                  <div className="rmacmp-when">
                    <input type="date" value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} required disabled={saving} aria-label="Start date" />
                    <input type="time" value={draft.startTime || ''} onChange={(e) => setDraft({ ...draft, startTime: e.target.value })} disabled={saving} aria-label="Start time" />
                  </div>
                </fieldset>
                <fieldset className="rmacmp-date-card is-end">
                  <legend>Closes · last moment to send</legend>
                  <div className="rmacmp-when">
                    <input type="date" value={draft.endDate} min={draft.startDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} required disabled={saving} aria-label="End date" />
                    <input type="time" value={draft.endTime || ''} onChange={(e) => setDraft({ ...draft, endTime: e.target.value })} disabled={saving} aria-label="End time" />
                  </div>
                </fieldset>
              </div>
              <p className="rmacmp-hint">Lebanon time. No time = opens at 12:00 AM and closes at midnight.</p>
              <label className="rmacmp-field">
                <span>The challenge</span>
                <textarea
                  rows={6}
                  maxLength={2000}
                  placeholder={'Make a game with a score counter.\n\nIdeas: catch the falling stars, a space shooter, a quiz…\nRules: your own project, made this month. Scratch, Python or any tool you learned.'}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  disabled={saving}
                />
              </label>
              <label className="rmacmp-field">
                <span>Prize (optional)</span>
                <input type="text" maxLength={200} value={draft.prize} onChange={(e) => setDraft({ ...draft, prize: e.target.value })} disabled={saving} />
              </label>
              {formError && <p className="rmacmp-error">{formError}</p>}
            </div>
            <footer>
              <button type="button" className="rmacmp-btn rmacmp-btn--ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmacmp-btn" disabled={saving}>
                {saving ? 'Saving…' : draft.id ? 'Save' : 'Create challenge'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {toast && (
        <div className={`rmacmp-toast ${toast.bad ? 'is-bad' : ''}`} role="status">
          <FontAwesomeIcon icon={toast.bad ? faTriangleExclamation : faCircleCheck} /> {toast.message}
        </div>
      )}
    </div>
  );
};

export default Competitions;
